import { UserRoles } from '../crawler.models';
import { CrawlfishJob, CreateCrawlfishJob, JobError, JobsRequestResponse, PagesRequestResponse, User } from '../crawler.interface';
import { _redirectToLogin, standardErrCheck } from './base.services';

const baseCfUrl = window._env_.VITE_BASE_CF_URL; // deployment
const basePlURL = window._env_.VITE_BASE_PL_URL; // PerformLine Server 

// *** USER *** // 
export const getLoggedInUser = async (): Promise<any> => {  // eslint-disable-line 
    console.log('Base Pline Url: ', basePlURL);
    console.log('Base CF Url: ', baseCfUrl);

    const urlString = `${basePlURL}/accounts/v1/me/`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
        } else {
            const userData = await resp.json();
            return userData.Results;
        }
    } catch (err) {
        console.error('Failed to get logged in user. Error: ', err);
        return null;
    }
};

export const getAllCrawlfishUsers = async (currentUserRole: string): Promise<User[] | []> => {
    if (currentUserRole !== UserRoles.Superuser) {
        return [];  // third-party users do not have access to /accounts/v1/users endpoint
    }
    const urlString = `${basePlURL}/accounts/v1/users/?q=crawlfish_user/true`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
            return [];
        } else {
            const usersData = await resp.json();
            return usersData.Results;
        }
    }
    catch (err) {
        console.error('Failed to get all Crawlfish users', err);
        return [];
    }
};
// -------------------- //


// *** JOBS *** //
export const _getAllJobs = async (filterString = ''): Promise<JobsRequestResponse | null> => {
    // jobs without names are never displayed, but this ActiveFilter is hidden
    const urlString = `${baseCfUrl}/crawlfish/v1/jobs?&q=name/not:${filterString}&limit=200`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
            return null;
        } else {
            const jobs = await resp.json();
            return jobs;
        }
    } catch (err) {
        console.error('Failed to get jobs. Error: ', err);
        return null;
    }
};

export const _getJobById = async (id: string): Promise<CrawlfishJob | null> => {
    const urlString = `${baseCfUrl}/crawlfish/v1/jobs/${id}`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
            return null;
        } else {
            const job = await resp.json();
            return job;
        }
    } catch (err) {
        console.error('Failed to get job via id. Error: ', err);
        return null;
    }
};

export const _createNewJob = async (newJob: CreateCrawlfishJob): Promise<CrawlfishJob> => {
    const resp = await fetch(`${baseCfUrl}/crawlfish/v1/jobs/create`, {
        'credentials': 'include',
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(newJob)
    });

    if (resp.status == 401) {
        _redirectToLogin();
    }

    const data = await resp.json();
    // Create Job specific error check - usually for failed params
    // example error {msg: 'invalid start page: HTTPS://INVALIDDOMAIN.COM'}
    if (data.msg && !resp.ok) {
        throw new Error(`Error creating job: ${data.msg}`);
    }
    await standardErrCheck(resp);
    return data;
};

export const _updateJob = async (updatedJob: CrawlfishJob): Promise<CrawlfishJob> => {
    // change name, owner, and reviewed
    const resp = await fetch(`${baseCfUrl}/crawlfish/v1/jobs/${updatedJob.id}`, {
        'credentials': 'include',
        method: 'PUT',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedJob)
    });

    if (resp.status == 401) {
        _redirectToLogin();
    }

    await standardErrCheck(resp);
    const data = await resp.json();
    return data;
};

export const _cancelJob = async (jobId: number): Promise<CrawlfishJob> => {
    const resp = await fetch(`${baseCfUrl}/crawlfish/v1/jobs/${jobId}/cancel`, {
        'credentials': 'include',
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    });

    if (resp.status == 401) {
        _redirectToLogin();
    }

    await standardErrCheck(resp);
    const data = await resp.json();
    return data;
};

export const _archiveJob = async (jobId: number): Promise<string> => {
    const resp = await fetch(`${baseCfUrl}/crawlfish/v1/jobs/${jobId}/archive`, {
        'credentials': 'include',
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    });

    if (resp.status == 401) {
        _redirectToLogin();
    }

    await standardErrCheck(resp);
    const data = await resp.json();
    return data;
};

export const _deleteJob = async (jobId: number): Promise<string | null> => {
    const urlString = `${baseCfUrl}/crawlfish/v1/jobs/${jobId}/delete`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
            return null;
        } else {
            const job = await resp.json();
            return job;
        }
    } catch (err) {
        console.error('Failed to delete job. Error: ', err);
        return null;
    }
};

export const _kickJob = async (id: number): Promise<any> => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const urlString = `${baseCfUrl}/crawlfish/v1/jobs/${id}/kick`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
        } else {
            const kickResponse = await resp.json();
            return kickResponse;
        }
    } catch (err) {
        console.error('Failed to kick job. Error: ', err);
        return null;
    }
};
// -------------------- //

// *** PAGES *** // 
export const _getPagesByJobId = async (id: string, filterString = ''): Promise<PagesRequestResponse | null> => {
    const urlString = `${baseCfUrl}/crawlfish/v1/pages/job/${id}?&q=uri/not:${filterString}&limit=200`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
            return null;
        } else {
            const pages = await resp.json();
            return pages;
        }
    } catch (err) {
        console.error('Failed to get pages by job id. Error: ', err);
        return null;
    }
};
// -------------------- //




// *** Misc *** //
export const _seeWorkers = async (): Promise<any> => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const urlString = `${baseCfUrl}/crawlfish/v1/workers`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
        } else {
            const workers = await resp.json();
            return workers;
        }
    } catch (err) {
        console.error('Failed to get worker info. Error: ', err);
        return null;
    }
};

export const _getJobErrors = async (id: string): Promise<JobError[] | []> => {
    const urlString = `${baseCfUrl}/crawlfish/v1/jobs/${id}/errors`;
    try {
        const resp = await fetch(urlString, { 'credentials': 'include' });
        if (resp.status === 401) {
            _redirectToLogin();
            return [];
        } else {
            const jobErrors = await resp.json();
            return jobErrors;
        }
    } catch (err) {
        console.error('Failed to get jobs error info. Error: ', err);
        return [];
    }
};